import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Lightbulb, TrendingDown, TrendingUp, AlertTriangle, Award, Clock, ChevronDown, ChevronUp } from 'lucide-react';

const typeConfig = {
  decline: { icon: TrendingDown, color: '#D4726A', bg: '#FEF2F2', label: 'Attention' },
  positive_streak: { icon: TrendingUp, color: '#2D8B5E', bg: '#ECFDF5', label: 'Momentum' },
  negative_streak: { icon: TrendingDown, color: '#BA7517', bg: '#FFFBEB', label: 'Pattern' },
  trajectory_gap: { icon: AlertTriangle, color: '#D4726A', bg: '#FEF2F2', label: 'Trajectory' },
  milestone_approaching: { icon: Clock, color: '#BA7517', bg: '#FFFBEB', label: 'Milestone' },
  celebration: { icon: Award, color: '#2D8B5E', bg: '#ECFDF5', label: 'Win' },
  inactivity: { icon: Clock, color: '#BA7517', bg: '#FFFBEB', label: 'Activity' },
};

export default function ReflectionCard({ divisionId }) {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!divisionId) return;
    api.get(`/reflections/${divisionId}`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [divisionId]);

  if (loading) return null;
  if (!data?.prompts?.length) return null;

  return (
    <div className="bg-white rounded-lg border overflow-hidden" style={{ borderColor: '#E5E1DC' }} data-testid="reflection-card">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        style={{ backgroundColor: '#FEFCE8', borderBottom: expanded ? '1px solid #E5E1DC' : 'none' }}
      >
        <div className="flex items-center gap-2">
          <Lightbulb size={16} style={{ color: '#BA7517' }} />
          <span className="text-sm font-semibold" style={{ color: '#1B2A4A' }}>Weekly Reflection</span>
          <span className="text-xs px-1.5 py-0.5 rounded-full" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
            {data.prompts.length} prompt{data.prompts.length > 1 ? 's' : ''}
          </span>
        </div>
        {expanded ? <ChevronUp size={16} style={{ color: '#6B7280' }} /> : <ChevronDown size={16} style={{ color: '#6B7280' }} />}
      </button>

      {expanded && (
        <div className="p-4 space-y-3">
          {data.prompts.map((prompt, idx) => {
            const cfg = typeConfig[prompt.type] || typeConfig.decline;
            const Icon = cfg.icon;
            return (
              <div key={idx} className="rounded-lg p-3" style={{ backgroundColor: cfg.bg }} data-testid={`reflection-prompt-${idx}`}>
                <div className="flex items-start gap-2">
                  <Icon size={15} style={{ color: cfg.color, marginTop: 2, flexShrink: 0 }} />
                  <div className="flex-1 min-w-0">
                    <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: cfg.color }}>{cfg.label}</span>
                    <p className="text-sm mt-1 leading-relaxed" style={{ color: '#1B2A4A' }}>{prompt.prompt_text}</p>
                    {prompt.action_prompt && (
                      <p className="text-xs mt-2 italic" style={{ color: '#6B7280' }}>{prompt.action_prompt}</p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
